/* One agent's suite — the one interface the Evals screens use.

   The suite is the API's (`shared/api/evals.ts`): cases and checks are written
   through it, a run is real agent runs graded by the eval worker, and the screens
   poll while one is going.

   Runs arrive as summaries; the ones a screen needs in full (the latest, the
   baseline, the open one, the one it is compared with) are loaded by id. */
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import {
  fromEvalCheck, toCaseCreate, toEvalCase, toEvalCheck, toEvalRun, useArchiveEvalCase, useCreateEvalCase,
  useEvalRuns, useEvalSuite, usePatchEvalCase, usePutEvalChecks, useSetEvalBaseline, useStartEvalRun,
} from "@/shared/api/evals";

import type { Agent, EvalCase, EvalCheck, EvalRun, EvalSuite } from "@/shared/types";

const SAVE_CHECKS_MS = 600; // the checks editor saves as you type; one PUT per pause

export interface Suite {
  evals: EvalSuite;
  /** Cases graded so far of the run in flight, or null. */
  progress: { done: number; total: number } | null;
  loading: boolean;
  /** A case as the suite has it now, or — archived since — as the run pinned it. */
  caseFor: (run: EvalRun | null | undefined, caseId: string) => EvalCase | null;
  saveCase: (kase: EvalCase) => void;
  deleteCase: (kase: EvalCase) => void;
  setChecks: (checks: EvalCheck[]) => void;
  runNow: () => void;
  setBaseline: (id: string) => void;
}

export default function useSuite(
  agent: Agent | null,
  { load = [], onFinished }: { load?: (string | null | undefined)[]; onFinished?: (run: EvalRun) => void } = {},
): Suite {
  const agentId = agent?.id ?? null;
  const query = useEvalSuite(agentId);
  const data = query.data;
  const baselineId = data?.baseline_run_id ?? null;
  const latestId = data?.runs[0]?.id ?? null;
  const ids = [...new Set([latestId, baselineId, ...load].filter((id): id is string => !!id))];
  const details = useEvalRuns(ids);
  const detailOf = new Map(details.flatMap((d) => (d.data ? [[d.data.id, d.data] as const] : [])));

  const createCase = useCreateEvalCase();
  const patchCase = usePatchEvalCase();
  const archiveCase = useArchiveEvalCase();
  const putChecks = usePutEvalChecks();
  const start = useStartEvalRun();
  const baseline = useSetEvalBaseline();

  // Suite-wide checks are edited in place: the draft is what the editor shows,
  // so a save that answers with new ids never remounts a field mid-typing.
  const [draftChecks, setDraftChecks] = useState<EvalCheck[] | null>(null);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(saveTimer.current), []);

  const evals: EvalSuite = {
    cases: (data?.cases ?? []).map(toEvalCase),
    checks: draftChecks ?? (data?.checks ?? []).map(toEvalCheck),
    runs: (data?.runs ?? []).map((summary) => toEvalRun(summary, detailOf.get(summary.id))),
    baselineRunId: baselineId,
  };

  /* A run started here says how it went when the eval worker closes it. */
  const started = useRef<string | null>(null);
  const latest = evals.runs[0];
  useEffect(() => {
    if (!latest || latest.id !== started.current || latest.status === "running") return;
    started.current = null;
    if (latest.status === "error") {
      toast.error("Eval run failed", { description: latest.error ?? undefined });
      return;
    }
    toast.success(`${latest.passed ?? 0}/${latest.total ?? 0} cases passed`, {
      description: latest.score == null ? undefined : `Score ${latest.score.toFixed(2)} on ${latest.target.version}`,
      action: onFinished ? { label: "Open", onClick: () => onFinished(latest) } : undefined,
    });
  }, [latest, onFinished]);

  const progress = start.isPending
    ? { done: 0, total: evals.cases.length }
    : latest?.status === "running" ? { done: latest.graded ?? 0, total: latest.total ?? 0 } : null;

  return {
    evals,
    progress,
    loading: query.isPending && !!agentId,
    caseFor: (run, caseId) => pinnedCase(evals, run, caseId),
    saveCase: (kase) => {
      if (!agentId) return;
      if (!evals.cases.some((c) => c.id === kase.id)) {
        createCase.mutate(toCaseCreate(agentId, kase));
        return;
      }
      const body = {
        name: kase.name, input: kase.input, expected: kase.expected, tags: kase.tags,
        checks: kase.checks.map(fromEvalCheck),
      };
      patchCase.mutate({ caseId: kase.id, body });
    },
    deleteCase: (kase) =>
      archiveCase.mutate(kase.id, {
        onSuccess: () =>
          toast.success("Case removed", {
            action: { label: "Undo", onClick: () => patchCase.mutate({ caseId: kase.id, body: { archived: false } }) },
          }),
      }),
    setChecks: (checks) => {
      if (!agentId) return;
      setDraftChecks(checks);
      clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => putChecks.mutate({ agentId, checks }), SAVE_CHECKS_MS);
    },
    runNow: () => {
      if (!agentId || progress) return;
      if (!evals.cases.length) {
        toast.error("Add a case first");
        return;
      }
      start.mutate({ agent_id: agentId }, { onSuccess: (run) => { started.current = run.id; } });
    },
    setBaseline: (id) =>
      baseline.mutate(id, {
        onSuccess: () => toast.success("Baseline set", { description: "Later runs are measured against it." }),
      }),
  };
}

function pinnedCase(evals: EvalSuite, run: EvalRun | null | undefined, caseId: string): EvalCase | null {
  const live = evals.cases.find((c) => c.id === caseId);
  if (live) return live;
  const pinned = run?.cases?.[caseId];
  if (!pinned) return null;
  const checks = run?.checks.find((c) => c.caseId === caseId)?.checks ?? [];
  return { id: caseId, ...pinned, tags: [], checks, origin: null, created: "" };
}
